import { Spacing } from '@persona-id/design-tokens';
import { useRef } from 'react';
import styled from 'styled-components';

import ContextMenu from 'dashboard/components/ContextMenu';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Permissioned from 'dashboard/components/Permissioned';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import FormAddAccountStatus, {
  FormAddAccountStatusData,
} from 'dashboard/forms/FormAddAccountStatus';
import FormRenameAccountStatus, {
  FormRenameAccountStatusData,
} from 'dashboard/forms/FormRenameAccountStatus';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import TableLayout, {
  TableActionTD,
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { AccountStatus } from 'dashboard/models/account-status';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Sticker from 'design-system/components/Sticker';
import IconPlus from 'images/icons/plus.svg';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

const DefaultSticker = styled(Sticker)`
  margin-left: ${Spacing[2]};
`;

const ArchivedSticker = styled(Sticker)`
  margin-left: ${Spacing[2]};
`;

interface AccountTypeStatusesTabProps {
  accountStatuses: AccountStatus[];
  defaultStatusToken: string;
  handleAddAccountStatus: (name: string) => Promise<void>;
  handleArchiveAccountStatus: (accountStatusId: string) => Promise<void>;
  handleRenameAccountStatus: (accountStatusId: string, name: string) => Promise<void>;
  handleRestoreAccountStatus: (accountStatusId: string) => Promise<void>;
  handleSetDefaultAccountStatus: (accountStatusId: string) => Promise<void>;
  isLoading: boolean;
  isSubmitting: boolean;
}

const AccountTypeStatusesTab = ({
  accountStatuses,
  defaultStatusToken,
  handleAddAccountStatus,
  handleArchiveAccountStatus,
  handleRenameAccountStatus,
  handleRestoreAccountStatus,
  handleSetDefaultAccountStatus,
  isLoading,
  isSubmitting,
}: AccountTypeStatusesTabProps) => {
  const selectedAccountStatusRef = useRef(null);
  const [
    addAccountStatusModal,
    { enable: showAddAccountStatusModal, disable: hideAddAccountStatusModal },
  ] = useToggle(false);
  const [
    renameAccountStatusModal,
    { enable: showRenameAccountStatusModal, disable: hideRenameAccountStatusModal },
  ] = useToggle(false);

  const onCreate = async (values: FormAddAccountStatusData) => {
    await handleAddAccountStatus(values.name);
    hideAddAccountStatusModal();
  };

  const onRename = async (values: FormRenameAccountStatusData) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    await handleRenameAccountStatus(selectedAccountStatusRef.current.id, values.name);
    hideRenameAccountStatusModal();
  };

  return (
    <>
      <ModalWrapper isOpen={addAccountStatusModal} handleCloseModal={hideAddAccountStatusModal}>
        <ModalContainer>
          <ModalHeader onClose={hideAddAccountStatusModal}>Add a new account status</ModalHeader>
          <ModalBody>
            <FormAddAccountStatus closeModal={hideAddAccountStatusModal} onSubmit={onCreate} />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
      <ModalWrapper
        isOpen={renameAccountStatusModal}
        handleCloseModal={hideRenameAccountStatusModal}
      >
        <ModalContainer>
          <ModalHeader onClose={hideRenameAccountStatusModal}>Rename account status</ModalHeader>
          <ModalBody>
            <FormRenameAccountStatus
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              initialValues={{ name: selectedAccountStatusRef.current?.attributes?.name }}
              closeModal={hideRenameAccountStatusModal}
              onSubmit={onRename}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <Card>
          <Card.Body>
            <Card.Title
              actions={
                <Permissioned permission={Permission.AccountTypeWrite}>
                  <Button
                    id='account-type-statuses-view__add-account-button'
                    variant='basic'
                    width='auto'
                    onClick={showAddAccountStatusModal}
                    prefixIcon={<IconPlus />}
                  >
                    Add Status
                  </Button>
                </Permissioned>
              }
            >
              Statuses
            </Card.Title>
            <TableLayout
              showHeader={true}
              dataTest='account-type-statuses-view__table-layout'
              headerTR={
                <>
                  <TableTH>Name</TableTH>
                  <TableTH>{/* Actions */}</TableTH>
                </>
              }
              nullState={
                <TableNullState dataTestBlock='account-type-statuses-view'>
                  <h3>Looks like there aren&#39;t any account statuses yet.</h3>
                  <p>Create an account status to get started.</p>
                </TableNullState>
              }
            >
              {accountStatuses.map((accountStatus: AccountStatus) => {
                const isDefault = accountStatus.id === defaultStatusToken;
                const isArchived = !!accountStatus.attributes['archived-at'];

                const contextMenuItems = [
                  {
                    id: 'rename',
                    onClick: () => {
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      selectedAccountStatusRef.current = accountStatus;
                      showRenameAccountStatusModal();
                    },
                    hidden: false,
                    label: 'Rename',
                  },
                ];
                if (!isDefault) {
                  if (isArchived) {
                    contextMenuItems.push({
                      id: 'restore',
                      onClick: () => {
                        handleRestoreAccountStatus(accountStatus.id);
                      },
                      hidden: false,
                      label: 'Restore',
                    });
                  } else {
                    contextMenuItems.push({
                      id: 'set-as-default',
                      onClick: () => {
                        handleSetDefaultAccountStatus(accountStatus.id);
                      },
                      hidden: false,
                      label: 'Set as Default',
                    });
                    contextMenuItems.push({
                      id: 'archive',
                      onClick: () => {
                        handleArchiveAccountStatus(accountStatus.id);
                      },
                      hidden: false,
                      label: 'Archive',
                    });
                  }
                }

                return (
                  <TableTR key={accountStatus.id}>
                    <TableTD>
                      {accountStatus.attributes.name}{' '}
                      {isDefault && <DefaultSticker>Default</DefaultSticker>}
                      {isArchived && <ArchivedSticker>Archived</ArchivedSticker>}
                    </TableTD>
                    <TableActionTD>
                      <ContextMenu
                        data-test={`account-type-statuses-view__context-menu-${accountStatus.id}`}
                        items={contextMenuItems}
                        disabled={isSubmitting}
                        renderItem={(item) => (
                          <ContextMenu.Item
                            id={`account-type-statuses-view__context-menu-${accountStatus.id}__rename`}
                            data-test={`account-type-statuses-view__context-menu-${accountStatus.id}__rename`}
                            key={item.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                              item.onClick();
                            }}
                          >
                            {item.label}
                          </ContextMenu.Item>
                        )}
                      />
                    </TableActionTD>
                  </TableTR>
                );
              })}
            </TableLayout>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default AccountTypeStatusesTab;
